// client/src/utils/pwaInstall.js
// PWA 설치 상태/프롬프트 관리.
// `beforeinstallprompt` 는 앱 로드 극초반에 한 번 발생하므로, 이 모듈을 index.js 에서
// import 해 최상단에서 리스너를 걸어 이벤트를 붙잡아 둔다(컴포넌트 마운트 전에 놓치지 않게).

let deferredPrompt = null;
const listeners = new Set();
const INSTALLED_KEY = 'wn_pwa_installed';

function notify() {
    listeners.forEach((fn) => {
        try { fn(); } catch { /* noop */ }
    });
}

// "이미 설치됨" 을 로컬에 기록 — getInstalledRelatedApps 가 자기 자신을 안 알려주는
// 브라우저에서도, 앱을 한 번 설치/실행하면 이후 탭에서 설치됨을 알 수 있게 한다.
function markInstalled() {
    try { window.localStorage.setItem(INSTALLED_KEY, '1'); } catch { /* noop */ }
}
function wasInstalledFlag() {
    try { return window.localStorage.getItem(INSTALLED_KEY) === '1'; } catch { return false; }
}

if (typeof window !== 'undefined') {
    window.addEventListener('beforeinstallprompt', (e) => {
        // 기본 설치 배너를 막고 우리 버튼으로 유도
        e.preventDefault();
        deferredPrompt = e;
        notify();
    });
    window.addEventListener('appinstalled', () => {
        deferredPrompt = null;
        markInstalled();
        notify();
    });
    // 설치된 앱(standalone)으로 열렸다면 그 사실을 기록 → 이후 브라우저 탭에서도 설치됨을 인식
    if (isStandalone()) markInstalled();
}

// Chrome/안드로이드에서 붙잡아 둔 설치 프롬프트가 있으면 true
export function canInstall() {
    return !!deferredPrompt;
}

// 이미 앱(standalone)으로 실행 중인지 — 설치 안내를 숨길 때 사용
export function isStandalone() {
    if (typeof window === 'undefined') return false;
    return (
        window.matchMedia?.('(display-mode: standalone)').matches ||
        window.navigator.standalone === true // iOS Safari
    );
}

// iOS(사파리)는 beforeinstallprompt 미지원 → "홈 화면에 추가" 수동 안내
export function isIOS() {
    if (typeof window === 'undefined') return false;
    const ua = window.navigator.userAgent || '';
    const iPhone = /iphone|ipad|ipod/i.test(ua);
    // iPadOS 13+ 는 기본 UA 가 Mac 데스크톱으로 위장(UA에 'iPad' 없음) → 터치 지원 Mac 은 iPad 로 간주
    const iPadOS = /Macintosh/.test(ua) && (window.navigator.maxTouchPoints || 0) > 1;
    return (iPhone || iPadOS) && !window.MSStream;
}

// macOS 데스크톱 사파리 — 파일 메뉴 → "Dock에 추가"(Safari 17/Sonoma+). iPad(터치 위장) 제외.
export function isMacSafari() {
    if (typeof window === 'undefined') return false;
    const ua = window.navigator.userAgent || '';
    const isMacDesktop = /Macintosh/.test(ua) && (window.navigator.maxTouchPoints || 0) <= 1;
    const isSafari = /Safari/.test(ua) && !/Chrome|Chromium|Edg|OPR|Brave/i.test(ua);
    return isMacDesktop && isSafari;
}

// 데스크톱 Firefox — PWA 설치 미지원(설치 UI 없음). 모바일 Firefox 는 제외(메뉴로 추가 가능).
export function isFirefoxDesktop() {
    if (typeof window === 'undefined') return false;
    const ua = window.navigator.userAgent || '';
    return /Firefox/i.test(ua) && !/Mobi|Android/i.test(ua);
}

// 설치 프롬프트 실행 (Chrome/안드로이드). 반환: 'accepted' | 'dismissed' | null(불가)
export async function promptInstall() {
    if (!deferredPrompt) return null;
    const evt = deferredPrompt;
    deferredPrompt = null; // 프롬프트는 1회성
    evt.prompt();
    let outcome = null;
    try {
        const choice = await evt.userChoice;
        outcome = choice?.outcome || null;
    } catch { /* noop */ }
    notify();
    return outcome;
}

// canInstall/standalone 상태 변화 구독 (설치 버튼 노출 갱신용). 반환: 해제 함수
export function subscribe(fn) {
    listeners.add(fn);
    return () => listeners.delete(fn);
}

// 브라우저 탭에서 열었어도 "이미 설치됨"을 감지 (Chromium 계열만 지원).
// iOS 사파리·Firefox 는 이 API 가 없어 항상 false → "파악되는 케이스만" 처리.
// standalone(설치된 앱 안에서 실행)은 별도로 isStandalone() 이 잡는다.
export async function isInstalledApp() {
    // 1) 앱 안에서 실행 중이거나(=설치됨) 2) 과거에 설치/실행 기록(플래그)이 있으면 설치된 것
    if (isStandalone()) { markInstalled(); return true; }
    if (wasInstalledFlag()) return true;
    // 3) Chromium 이면 getInstalledRelatedApps 로 추가 확인(자기 자신을 알려주는 버전에 한함)
    try {
        if (typeof navigator !== 'undefined' && navigator.getInstalledRelatedApps) {
            const apps = await navigator.getInstalledRelatedApps();
            // related_applications 를 선언 안 했으므로, 반환되는 건 우리 PWA 자신뿐 → 하나라도 있으면 설치된 것
            if (Array.isArray(apps) && apps.length > 0) { markInstalled(); return true; }
        }
    } catch { /* noop */ }
    return false;
}
